import { RefObject } from 'react';
import { Box, Paper, useTheme, Divider, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import RemoteNodeForm, { RemoteNodeFormRef } from './RemoteNodeForm';
import BaseNodeForm, {
  BaseNodeFormRef,
  BaseNodeUpdateData,
} from './BaseNodeForm';
import ActionButtons from './ActionButtons';

export interface FormPanelProps {
  isMinimized: boolean;
  onToggleMinimize: () => void;
  operator?: string | null;
  operatorKey?: string | null;
  remoteNodeFormRef: RefObject<RemoteNodeFormRef | null>;
  baseNodeFormRef: RefObject<BaseNodeFormRef | null>;
  onClearRNMarker: () => void;
  onUpdateSector: (sector: string) => void;
  onUpdateBaseNodeFields: (data: BaseNodeUpdateData) => void;
  onCalculatePathLoss: () => void;
  onToggleBNRadius: () => void;
  onClearSectorResults?: () => void;
  sectorLoading: boolean;
  showSectorsButton: boolean;
  showRemoveButton: boolean;
  showClearButton: boolean;
  sectorsButtonLabel: string;
}

const FormPanel = ({
  isMinimized,
  onToggleMinimize,
  operator,
  operatorKey,
  remoteNodeFormRef,
  baseNodeFormRef,
  onClearRNMarker,
  onUpdateSector,
  onUpdateBaseNodeFields,
  onCalculatePathLoss,
  onToggleBNRadius,
  onClearSectorResults,
  sectorLoading,
  showSectorsButton,
  showRemoveButton,
  showClearButton,
  sectorsButtonLabel,
}: FormPanelProps) => {
  const theme = useTheme();

  if (isMinimized) {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 1000,
        }}
      >
        <IconButton
          onClick={onToggleMinimize}
          sx={{
            backgroundColor: theme.palette.background.paper,
            '&:hover': {
              backgroundColor: theme.palette.background.paper,
            },
          }}
          aria-label="Expand forms"
        >
          <MenuIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'absolute',
        top: 16,
        left: 16,
        width: { xs: 'calc(100% - 32px)', sm: 400 },
        maxWidth: { xs: 'none', sm: 400 },
        maxHeight: 'calc(100vh - 96px)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        backgroundColor: theme.palette.background.paper,
        zIndex: 1000,
      }}
    >
      <IconButton
        size="small"
        onClick={onToggleMinimize}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          p: 0.5,
          zIndex: 1001,
        }}
        aria-label="Minimize forms"
      >
        <ExpandMoreIcon fontSize="small" />
      </IconButton>

      <Box
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
          flex: 1,
          p: 3,
          pb: 0,
        }}
      >
        <RemoteNodeForm
          ref={remoteNodeFormRef}
          onClearRNMarker={onClearRNMarker}
        />

        <Divider sx={{ my: 3 }} />

        {operator && operatorKey && (
          <BaseNodeForm
            ref={baseNodeFormRef}
            operatorKey={operatorKey}
            operator={operator}
            onUpdateSector={onUpdateSector}
            onUpdateFields={onUpdateBaseNodeFields}
          />
        )}
      </Box>

      <ActionButtons
        onCalculatePathLoss={onCalculatePathLoss}
        onToggleBNRadius={onToggleBNRadius}
        onClearRNMarker={onClearRNMarker}
        onClearSectorResults={onClearSectorResults}
        sectorLoading={sectorLoading}
        showSectorsButton={showSectorsButton}
        showRemoveButton={showRemoveButton}
        showClearButton={showClearButton}
        sectorsButtonLabel={sectorsButtonLabel}
      />
    </Paper>
  );
};

export default FormPanel;
